import { GetCurrencyReturn } from '@web/walter-lib/api'
import { useCurrencies, useReceiveFiatList } from '~api'
import { useReceiveVerificationLogic } from '~hooks/useReceiveVerificationLogic'
import { useAssetLimitsLogic } from '~hooks/useAssetLimitsLogic'

// eslint-disable-next-line complexity
export const useIsReceiveDisabled = (currencyCode?: string) => {
    const { data: currencies = {}, isLoading: isCurrenciesLoading, error: currenciesError } = useCurrencies()
    const { data: fiatCurrencyCodes = [], isLoading: isFiatLoading, error: fiatListError } = useReceiveFiatList()
    const limitsLogic = useAssetLimitsLogic()
    const verificationLogic = useReceiveVerificationLogic()

    const isLoading = isCurrenciesLoading || isFiatLoading || verificationLogic.isLoading || limitsLogic.isLoading
    const error = currenciesError || fiatListError || verificationLogic.error || limitsLogic.error

    if (isLoading) {
        return { isLoading, isDisabled: true, error }
    }

    // eslint-disable-next-line complexity
    const checkIsCoinReceiveDisabled = (code: string) => {
        const currency: GetCurrencyReturn | undefined = currencies[code]

        if (!currency) {
            return true
        }

        if (currency.delisted) {
            return true
        }

        const existInFiatList = fiatCurrencyCodes.includes(code)

        if (currency.crypto) {
            if (verificationLogic.disabled) {
                return true
            }

            const payinEnabled = currency.payin_enabled && currency.networks?.some(network => network.payin_enabled)
            const manualFiatPayinEnabled = currency.manual_fiat_payin_enabled && existInFiatList

            return !payinEnabled && !manualFiatPayinEnabled
        }

        if (limitsLogic.disabled) {
            return true
        }

        return !existInFiatList
    }

    const isDisabled = currencyCode
        ? checkIsCoinReceiveDisabled(currencyCode)
        : verificationLogic.disabled || Object.keys(currencies).every(code => checkIsCoinReceiveDisabled(code))

    const isKycDisabled = (() => {
        if (currencyCode) {
            return currencies?.[currencyCode]?.crypto && verificationLogic.disabled
        }

        return verificationLogic.disabled
    })()

    const isFiatDisabled = verificationLogic.disabled || limitsLogic.disabled

    return {
        error,
        isLoading,
        isDisabled,
        isKycDisabled,
        isFiatDisabled,
        checkIsCoinReceiveDisabled,
    }
}
